import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();

  const redirectTo = location.state?.from || "/frameworks";

  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      // Authenticate user
      const response = await axios.post(`${process.env.REACT_APP_API_URL}token/`, {
        username: formData.username,
        password: formData.password, // never console.log this
      });

      const { access, refresh } = response.data;

      // Fetch user profile
      const profileRes = await axios.get(
        `${process.env.REACT_APP_API_URL}users/user/profile/`,
        { headers: { Authorization: `Bearer ${access}` } }
      );

      const userProfile = profileRes.data;

      // Update context and localStorage
      login(userProfile, access, refresh);

      // Redirect
      if (userProfile.is_superuser) navigate("/cobit/dashboard", { replace: true });
      else navigate(redirectTo, { replace: true });
    } catch (err) {
      // Clear any invalid tokens
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_profile");

      setError("Invalid username or password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-orange-50 px-4">
      <Helmet>
        <title>Login | Governance Tool</title>
      </Helmet>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-[#FF4D00]">Welcome Back</h2>
          <p className="text-sm text-gray-500 mt-2">
            Enter your credentials to access your assessments.
          </p>
        </div>

        {error && <p className="text-red-500 text-center text-sm mb-4">{error}</p>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-gray-700 font-medium mb-2">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="Enter username"
              required
              autoComplete="username"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                required
                autoComplete="current-password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#FF4D00] focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-[#FF4D00] text-white py-2 rounded-lg font-semibold hover:bg-orange-700 transition duration-200"
          >
            Login
          </button>

          <p className="text-right mt-2">
            <a href="/forgot-password" className="text-[#FF4D00] text-sm hover:underline">
              Forgot Password?
            </a>
          </p>
        </form>

        <div className="mt-6 text-center text-gray-500 text-sm">
          <p>
            New to the platform? Contact your administrator to request access.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
